import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UNIT_OPTIONS = [
  { value: "nos",      label: "Nos" },
  { value: "sqft",     label: "Sq.Ft" },
  { value: "rmt",      label: "Rmt (Running Meter)" },
  { value: "days",     label: "Days" },
  { value: "hours",    label: "Hours" },
  { value: "lump sum", label: "Lump Sum" },
  { value: "kg",       label: "Kg" },
  { value: "liters",   label: "Liters" },
];

const emptyItem = () => ({
  description: "", unit: "nos", quantity: 1,
  rate: 0, discount_percent: 0, discount_value: 0,
  gst_percent: 0, gst_value: 0, final_total: 0,
});

const WorkOrderForm = () => {
  const [vendors, setVendors]           = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetails, setVendorDetails]   = useState(null);
  const [woItems, setWoItems]           = useState([emptyItem()]);
  const [woNumber, setWoNumber]         = useState("");
  const [woDate, setWoDate]             = useState(new Date());
  const [title, setTitle]               = useState("");
  const [location, setLocation]         = useState("");
  const [startDate, setStartDate]       = useState(null);
  const [endDate, setEndDate]           = useState(null);
  const [terms, setTerms]               = useState("");
  const [status, setStatus]             = useState("Pending");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [attachment, setAttachment]     = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");

  const navigate = useNavigate();
  const { id }   = useParams();
  const user     = JSON.parse(localStorage.getItem("user"));
  const isEditable = status === "Pending" && (user?.role === "store_manager" || user?.role === "superadmin");

  /* ── Generate WO Number ── */
  const generateWONumber = async () => {
    try {
      const now      = new Date();
      const month    = now.getMonth() + 1;
      const year     = now.getFullYear();
      const fyStart  = month < 4 ? year - 1 : year;
      const fyLabel  = `${fyStart}-${String(fyStart + 1).slice(2)}`;
      const branch   = JSON.parse(localStorage.getItem("activeBranch") || "{}");
      const code     = branch?.branch_code || "HIS";
      const res      = await axios.get("/work-orders/last-number");
      const next     = res.data?.lastNumber ? parseInt(res.data.lastNumber) + 1 : 1;
      return `WO/${code}/${fyLabel}/${String(next).padStart(4, "0")}`;
    } catch {
      return "WO/HIS/0000-00/0000";
    }
  };

  /* ── Load vendors ── */
  useEffect(() => {
    axios.get("/vendors/dropdown").then(res => {
      if (Array.isArray(res.data.vendors)) setVendors(res.data.vendors);
    }).catch(console.error);
  }, []);

  /* ── Load existing WO if editing ── */
  useEffect(() => {
    if (id) {
      setLoading(true);
      axios.get(`/work-orders/${id}`).then(res => {
        const { order, items } = res.data;
        setWoNumber(order.wo_number);
        setTitle(order.title || "");
        setLocation(order.location || "");
        setTerms(order.terms_conditions || "");
        setStatus(order.status || "Pending");
        setExistingAttachment(order.attachment_path ? {
          path: order.attachment_path,
          name: order.attachment_original_name || "Work Order Attachment.pdf",
        } : null);
        setWoDate(new Date(order.wo_date));
        if (order.start_date) setStartDate(new Date(order.start_date));
        if (order.end_date)   setEndDate(new Date(order.end_date));
        const v = vendors.find(v => v.id === order.vendor_id);
        if (v) { setSelectedVendor({ label: v.name, value: v.id, ...v }); setVendorDetails(v); }
        setWoItems(items.map(item => ({
          description:      item.description,
          unit:             item.unit || "nos",
          quantity:         item.qty,
          rate:             item.rate,
          discount_percent: item.discount_percent,
          discount_value:   item.discount_total,
          gst_percent:      item.gst_percent,
          gst_value:        item.gst_total,
          final_total:      item.final_total,
        })));
      }).catch(console.error).finally(() => setLoading(false));
    } else {
      generateWONumber().then(setWoNumber);
    }
  }, [id, vendors]);

  /* ── Recalculate row ── */
  const handleItemChange = (index, field, value) => {
    if (!isEditable) return;
    const updated = [...woItems];
    updated[index][field] = value;

    const qty      = parseFloat(updated[index].quantity       || 0);
    const rate     = parseFloat(updated[index].rate           || 0);
    const discPct  = parseFloat(updated[index].discount_percent || 0);
    const gstPct   = parseFloat(updated[index].gst_percent    || 0);

    const total      = qty * rate;
    const discVal    = (total * discPct) / 100;
    const taxable    = total - discVal;
    const gstVal     = (taxable * gstPct) / 100;
    updated[index].discount_value = discVal;
    updated[index].gst_value      = gstVal;
    updated[index].final_total    = taxable + gstVal;
    setWoItems(updated);
  };

  const addItem    = () => isEditable && setWoItems([...woItems, emptyItem()]);
  const removeItem = (i) => { if (!isEditable) return; const u = [...woItems]; u.splice(i, 1); setWoItems(u); };

  /* ── Totals ── */
  const totals = woItems.reduce((acc, item) => {
    acc.subtotal += parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
    acc.discount += parseFloat(item.discount_value || 0);
    acc.gst      += parseFloat(item.gst_value || 0);
    return acc;
  }, { subtotal: 0, discount: 0, gst: 0 });
  const grandTotal = totals.subtotal - totals.discount + totals.gst;

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAttachmentError("");
    if (!file) return setAttachment(null);
    if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
      setAttachment(null);
      e.target.value = "";
      return setAttachmentError("Only a PDF file is allowed.");
    }
    if (file.size > 2 * 1024 * 1024) {
      setAttachment(null);
      e.target.value = "";
      return setAttachmentError("PDF attachment must be 2 MB or smaller.");
    }
    setAttachment(file);
    setRemoveAttachment(false);
  };

  const downloadAttachment = async () => {
    try {
      const response = await axios.get(`/work-orders/${id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = existingAttachment?.name || "Work_Order_Attachment.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download Work Order attachment.");
    }
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable) return;
    if (!selectedVendor) return alert("Please select a contractor/vendor.");
    if (!title.trim())   return alert("Please enter a work subject/title.");

    const validItems = woItems.filter(item =>
      item.description.trim() && parseFloat(item.quantity) > 0 && parseFloat(item.rate) > 0
    );
    if (validItems.length === 0) return alert("Please add at least one valid work item.");

    const payload = {
      wo_number:         woNumber,
      vendor_id:         selectedVendor.id,
      wo_date:           moment(woDate).format("YYYY-MM-DD"),
      title,
      location,
      start_date:        startDate ? moment(startDate).format("YYYY-MM-DD") : null,
      end_date:          endDate   ? moment(endDate).format("YYYY-MM-DD")   : null,
      terms_conditions:  terms,
      created_by:        user.id,
      items: validItems.map(item => ({
        description:      item.description,
        unit:             item.unit,
        qty:              item.quantity,
        rate:             item.rate,
        total:            parseFloat(item.quantity) * parseFloat(item.rate),
        discount_percent: item.discount_percent,
        discount_total:   item.discount_value,
        gst_percent:      item.gst_percent,
        gst_total:        item.gst_value,
        final_total:      item.final_total,
      })),
      subtotal:      totals.subtotal,
      discount_total: totals.discount,
      gst_total:     totals.gst,
      grand_total:   grandTotal,
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, key === "items" ? JSON.stringify(value) : (value ?? ""));
    });
    if (attachment) formData.append("attachment", attachment);
    if (id) formData.append("remove_attachment", removeAttachment ? "1" : "0");

    try {
      setIsSubmitting(true);
      if (id) {
        await axios.put(`/work-orders/${id}`, formData);
      } else {
        await axios.post("/work-orders", formData);
      }
      navigate("/dashboard/work-orders");
    } catch (err) {
      console.error("WO submit error:", err?.response?.data || err.message);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to save Work Order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary"></i></div>;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>{id ? "Edit Work Order" : "Create Work Order"}</h4>
          <p>{id ? "Update scope of work and details" : "Raise a new work order for a contractor"}</p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/work-orders")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="card shadow-sm p-4" style={{ border: "none", borderRadius: "12px" }}>

        {/* ── Top row: Vendor, WO#, WO Date ── */}
        <div className="row">
          <div className="col-md-5 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Contractor / Vendor <span className="text-danger">*</span></label>
            <Select
              value={selectedVendor}
              options={vendors.map(v => ({ label: v.name, value: v.id, ...v }))}
              onChange={opt => {
                if (!isEditable) return;
                setSelectedVendor(opt);
                setVendorDetails(opt);
              }}
              isDisabled={!isEditable}
              isClearable={isEditable}
              placeholder="Select contractor..."
            />
            {vendorDetails && (
              <div className="alert alert-light border p-2 mt-2" style={{ fontSize: "12px" }}>
                <strong>Address:</strong> {vendorDetails.address}<br />
                <strong>Contact:</strong> {vendorDetails.contact_person} ({vendorDetails.phone})<br />
                <strong>GST/PAN:</strong> {vendorDetails.pan_gst}
              </div>
            )}
          </div>

          <div className="col-md-3 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>WO Number</label>
            <input type="text" className="form-control form-control-sm" value={woNumber} readOnly
              style={{ background: "#f8fafc", fontWeight: 700 }} />
          </div>

          <div className="col-md-3 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>WO Date</label>
            <DatePicker
              selected={woDate instanceof Date && !isNaN(woDate) ? woDate : new Date()}
              onChange={date => isEditable && setWoDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control form-control-sm"
              readOnly={!isEditable}
            />
          </div>
        </div>

        {/* ── Work details ── */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Subject / Title of Work <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. Painting of Block A Classrooms"
              value={title}
              onChange={e => isEditable && setTitle(e.target.value)}
              readOnly={!isEditable}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Location</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="e.g. Block A, 2nd Floor"
              value={location}
              onChange={e => isEditable && setLocation(e.target.value)}
              readOnly={!isEditable}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Start Date</label>
            <DatePicker
              selected={startDate}
              onChange={date => isEditable && setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control form-control-sm"
              placeholderText="Select start date"
              readOnly={!isEditable}
              isClearable={isEditable}
            />
          </div>
          <div className="col-md-3 mb-3">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>End Date</label>
            <DatePicker
              selected={endDate}
              onChange={date => isEditable && setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control form-control-sm"
              placeholderText="Select end date"
              readOnly={!isEditable}
              isClearable={isEditable}
              minDate={startDate}
            />
          </div>
        </div>

        {/* ── Scope of Work Table ── */}
        <div className="table-responsive mt-2">
          <table className="table table-bordered" style={{ fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ minWidth: "220px" }}>Description of Work <span className="text-danger">*</span></th>
                <th style={{ width: "110px" }}>Unit</th>
                <th style={{ width: "75px" }}>Qty</th>
                <th style={{ width: "90px" }}>Rate (₹)</th>
                <th style={{ width: "75px" }}>Disc %</th>
                <th style={{ width: "80px" }}>Discount</th>
                <th style={{ width: "75px" }}>GST %</th>
                <th style={{ width: "80px" }}>GST</th>
                <th style={{ width: "100px" }}>Final Total</th>
                {isEditable && <th style={{ width: "50px" }}></th>}
              </tr>
            </thead>
            <tbody>
              {woItems.map((item, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Painting of classrooms"
                      value={item.description}
                      onChange={e => handleItemChange(index, "description", e.target.value)}
                      disabled={!isEditable}
                    />
                  </td>
                  <td>
                    <select
                      className="form-control form-control-sm"
                      value={item.unit}
                      onChange={e => handleItemChange(index, "unit", e.target.value)}
                      disabled={!isEditable}
                    >
                      {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </td>
                  <td><input type="number" className="form-control form-control-sm" value={item.quantity} min="0" onChange={e => handleItemChange(index, "quantity", e.target.value)} disabled={!isEditable} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={item.rate} min="0" onChange={e => handleItemChange(index, "rate", e.target.value)} disabled={!isEditable} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={item.discount_percent} min="0" max="100" onChange={e => handleItemChange(index, "discount_percent", e.target.value)} disabled={!isEditable} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={Number(item.discount_value || 0).toFixed(2)} readOnly style={{ background: "#f8fafc" }} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={item.gst_percent} min="0" onChange={e => handleItemChange(index, "gst_percent", e.target.value)} disabled={!isEditable} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={Number(item.gst_value || 0).toFixed(2)} readOnly style={{ background: "#f8fafc" }} /></td>
                  <td><input type="number" className="form-control form-control-sm" value={Number(item.final_total || 0).toFixed(2)} readOnly style={{ background: "#f8fafc", fontWeight: 600 }} /></td>
                  {isEditable && (
                    <td className="text-center">
                      <button type="button" className="ims-btn ims-btn-danger" onClick={() => removeItem(index)} title="Remove">
                        <i className="fas fa-times"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditable && (
            <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={addItem}>
              <i className="fas fa-plus mr-1"></i> Add Row
            </button>
          )}
        </div>

        {/* ── Terms + Totals ── */}
        <div className="row mt-2">
          <div className="col-md-5">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Terms & Conditions</label>
            <textarea
              className="form-control form-control-sm"
              rows="3"
              value={terms}
              maxLength={250}
              onChange={e => isEditable && setTerms(e.target.value.slice(0, 250))}
              readOnly={!isEditable}
              placeholder="Payment terms, warranty conditions, etc."
            />
            {isEditable && (
              <small className="text-muted d-block text-right mt-1">
                {Math.min(terms.length, 250)}/250 characters
              </small>
            )}
          </div>
          <div className="col-md-4 offset-md-3">
            <table className="table table-bordered" style={{ fontSize: "13px" }}>
              <tbody>
                <tr><td>Subtotal</td><td className="text-right"><strong>₹{totals.subtotal.toFixed(2)}</strong></td></tr>
                <tr><td>Discount</td><td className="text-right">₹{totals.discount.toFixed(2)}</td></tr>
                <tr><td>GST</td><td className="text-right">₹{totals.gst.toFixed(2)}</td></tr>
                <tr style={{ background: "#f0f9ff" }}>
                  <td><strong>Grand Total</strong></td>
                  <td className="text-right"><strong style={{ color: "#0f172a", fontSize: "15px" }}>₹{grandTotal.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <label style={{ fontWeight: 600, fontSize: "13px" }}>Work Order Attachment</label>
            <input
              type="file"
              className="form-control form-control-sm"
              accept="application/pdf,.pdf"
              onChange={handleAttachmentChange}
              disabled={!isEditable}
            />
            <small className="text-muted">Optional: one PDF only, maximum 2 MB.</small>
            {attachment && <small className="text-success d-block mt-1">Selected: {attachment.name}</small>}
            {attachmentError && <small className="text-danger d-block mt-1">{attachmentError}</small>}
            {existingAttachment && !removeAttachment && !attachment && (
              <div className="mt-2">
                <button type="button" className="btn btn-sm btn-outline-primary mr-2" onClick={downloadAttachment}>
                  <i className="fas fa-file-pdf mr-1"></i>{existingAttachment.name}
                </button>
                {isEditable && (
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRemoveAttachment(true)}>
                    Remove
                  </button>
                )}
              </div>
            )}
            {removeAttachment && !attachment && (
              <small className="text-warning d-block mt-2">
                Existing attachment will be removed.{' '}
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setRemoveAttachment(false)}>Undo</button>
              </small>
            )}
          </div>
        </div>

        {isEditable && (
          <div className="text-end mt-2">
            <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={() => navigate("/dashboard/work-orders")}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={isSubmitting}>
              <i className={`fas ${isSubmitting ? "fa-spinner fa-spin" : "fa-save"} mr-1`}></i>
              {isSubmitting ? "Saving..." : (id ? "Update Work Order" : "Create Work Order")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default WorkOrderForm;
